import { ContextConsumer } from '@lit/context'
import { LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { injectLightDomStyles } from '../common/light-dom-styles.js'
import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import {
  requestResizableContextAfterParentUpgrade,
  resizableContext,
} from './_ResizableContext.js'
import style from './Resizable.light-dom.css'

const scopeKey = 'nord-resizable'
let panelIdCounter = 0

/**
 * Resizable Panel contains consumer-owned content whose percentage size is
 * coordinated by its direct Resizable parent.
 *
 * @status draft
 * @category layout
 * @parent Resizable
 * @lightDom true
 *
 * @slot - Consumer-owned panel content.
 *
 * @cssprop [--n-resizable-panel-overflow=auto] - Controls overflow of panel content.
 */
@customElement('nord-resizable-panel')
export default class ResizablePanel extends DraftComponentMixin(LitElement) {
  /** Stable state identifier. Defaults to the panel's DOM id or a generated id. */
  @property({ attribute: 'panel-id', reflect: true }) panelId?: string

  /** Initial percentage used when no group default layout supplies this panel. */
  @property({ attribute: 'default-size', reflect: true, type: Number }) defaultSize?: number

  /** Minimum expanded percentage. */
  @property({ attribute: 'min-size', reflect: true, type: Number }) minSize = 0

  /** Maximum percentage. */
  @property({ attribute: 'max-size', reflect: true, type: Number }) maxSize = 100

  /** Allows the panel to snap below its minimum to `collapsed-size`. */
  @property({ reflect: true, type: Boolean }) collapsible = false

  /** Percentage used while collapsed. */
  @property({ attribute: 'collapsed-size', reflect: true, type: Number }) collapsedSize = 0

  private readonly generatedId = `n-resizable-panel-${++panelIdCounter}`
  private context = new ContextConsumer(this, {
    context: resizableContext,
    subscribe: true,
    callback: context => context?.registerPanel(this),
  })

  connectedCallback() {
    super.connectedCallback()
    injectLightDomStyles(this, style, scopeKey)
    if (!this.id) {
      this.id = this.generatedId
    }
    this.context.value?.registerPanel(this)
    if (!this.context.value) {
      requestResizableContextAfterParentUpgrade(this, () => this.context.hostConnected())
    }
  }

  disconnectedCallback() {
    this.context.value?.unregisterPanel(this)
    super.disconnectedCallback()
  }

  protected createRenderRoot() {
    return this
  }

  protected updated() {
    this.context.value?.notifyPanel(this)
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-resizable-panel': ResizablePanel
  }
}
